import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createAssignmentsSchema,
  CreateAssignmentsSchema,
  defaultValues,
} from '../../models';
import { useAssignmentsActions } from '../../hooks';
import {
  FormSubmitButton,
  GlobalButton,
  NavigateLinks,
  DateInput,
  Authorize,
} from '@/components';
import { assignmentsPageBreadcrumbs } from '../../config';
import { SearchUser, AssetSelector, AssetDisplay, AssetSelectorData } from '../../../../components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { ROLES } from '@/components/security/AppRoles';
import { ErrorPage } from '@/pages/Layout/pages';

import styles from './CreateAssignments.module.css';

const CreateAssignments = () => {
  const navigate = useNavigate();

  const { handleSubmit, control, reset, setValue } =
    useForm<CreateAssignmentsSchema>({
      resolver: zodResolver(createAssignmentsSchema),
      defaultValues,
    });

  const [selectedAsset, setSelectedAsset] = useState<AssetSelectorData | null>(null);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const [returnedAt, setReturnedAt] = useState<Date | null>(null);

  const breadcrumbs = useMemo(() => assignmentsPageBreadcrumbs(navigate), []);
  const { onCreateAssignment, isCreatingAssignment } = useAssignmentsActions();

  const onSubmit = async (data: CreateAssignmentsSchema) => {
    const today = new Date().toISOString().split('T')[0];
    const payload = {
      ...data,
      assignedAt: today,
      returnedAt: returnedAt || null,
    };
    await onCreateAssignment(payload, reset);
    setReturnedAt(null);
  };

  const onAssetSelectedFromModal = (asset: AssetSelectorData) => {
    setSelectedAsset(asset);
    setValue('assetId', asset.id, { shouldValidate: true });
  };

  return (
    <Authorize
      permission={ROLES.INVENTORY.admin}
      fallback={<ErrorPage code={403} />}
    >
      <section className={styles.container}>
        <NavigateLinks actualPage='Crear asignación' breadcrumbs={breadcrumbs} />
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <h2 className={styles.formTitle}>Crear asignación</h2>
          <AssetDisplay
            selectedAsset={selectedAsset}
            onClear={() => {
              setSelectedAsset(null);
              setValue('assetId', '', { shouldValidate: true });
            }}
            onSearch={() => setAssetSelectorOpen(true)}
            showDetails={true}
            showWarning={true}
          />
          <AssetSelector
            open={assetSelectorOpen}
            onClose={() => setAssetSelectorOpen(false)}
            onSelect={onAssetSelectedFromModal}
          />
          <SearchUser
            name='userId'
            placeholder='Usuario'
            control={control}
            required
            onUserSelect={(userId) => {
              setValue('userId', userId, {
                shouldValidate: true,
              });
            }}
            showSelectedUserInfo={true}
          />
          <div className='flex justify-start mb-4'>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={() => navigate('/inventory/users/create?from=assignments')}
            >
              <PersonAddIcon />
              Crear usuario
            </GlobalButton>
          </div>
          <DateInput
            name='returnedAt'
            placeholder='Fecha de devolución'
            value={returnedAt}
            onChange={setReturnedAt}
          />
          <FormSubmitButton
            isLoading={isCreatingAssignment}
            label='Crear'
            icon={' '}
            action={handleSubmit(onSubmit)}
          ></FormSubmitButton>
        </form>
      </section>
    </Authorize>
  );
};

export default CreateAssignments;
